import { makeAutoObservable } from "mobx";
import { basketMaxItemsCount } from "../utils/consts";
import basketApi from "../api/basketApi";
import { fetchWithAbort } from "../utils/fetchWithAbort";

// basketStore.items example:
// [
//   {
//       "ownerSteamId": "7656112427424562",
//       "shopItem": {
//           "nameRu": "Дейнозух",
//           "nameEn": "Deinosuchus",
//           "teeth": 300,
//           "type": "Динозавр",
//           "category": "Плотоядные",
//           "image": "https://abcdef.com/api/resources/dino_images/дейн.png",
//           "description": "",
//           "isVisible": true,
//           "itemId": 1
//       },
//       "amount": 3,
//       "basketIds": [
//           51,
//           52,
//           53
//       ]
//   },
//   ... others
// ]

class BasketStore {
  items = {}; // ключ - id, значение - объект с данными товара
  checkedItemsIds = new Set(); // содержит id выбранных товаров
  totalPriceOa = 0;
  totalPriceTeeth = 0;
  totalBasketItemsCount = 0;

  constructor() {
    makeAutoObservable(this);
  }

  // Установка всех товаров в корзину
  setItems(items) {
    this.items = items;
    this.updatePrices();
    this.updateTotalBasketCount();
  }

  // Установка цен
  setTotalPriceOa(oa) {
    this.totalPriceOa = oa;
  }

  setTotalPriceTeeth(teeth) {
    this.totalPriceTeeth = teeth;
  }

  // Добавление одного товара в корзину
  async addToBasket(shopItem, signal) {
    try {
      if (this.totalBasketItemsCount >= basketMaxItemsCount) {
        throw new Error(
          `Нельзя добавить больше ${basketMaxItemsCount} товаров.`
        );
      }

      const item = this.items[shopItem.itemId];
      const newAmount = Math.min(
        (item?.amount || 0) + 1,
        basketMaxItemsCount - this.totalBasketItemsCount + (item?.amount || 0)
      );

      console.log("this.totalBasketItemsCount: ", this.totalBasketItemsCount);
      console.log("newAmount: ", newAmount);
      console.log(
        "newAmount - (item?.amount || 0): ",
        newAmount - (item?.amount || 0)
      );
      console.log("\n");

      if (newAmount > (item?.amount || 0)) {
        await fetchWithAbort(
          (signal) =>
            basketApi.addEvrimaBasketId(
              shopItem.itemId,
              newAmount - (item?.amount || 0),
              signal
            ),
          signal
        );

        const updatedItems = {
          ...this.items,
          [shopItem.itemId]: { ...item, shopItem, amount: newAmount },
        };
        this.setItems(updatedItems);
      }
    } catch (error) {
      console.error("addToBasket error:", error);
      throw error;
    }
  }

  // Удаление одного экземпляра товара
  async removeOneFromBasket(itemId, signal) {
    try {
      const item = this.items[itemId];
      if (item) {
        await fetchWithAbort(
          (signal) => basketApi.deleteEvrimaBasketId(itemId, 1, signal),
          signal
        );

        const updatedItems = { ...this.items };
        if (item.amount > 1) {
          updatedItems[itemId] = { ...item, amount: item.amount - 1 };
        } else {
          delete updatedItems[itemId];
        }
        this.setItems(updatedItems);
      }
    } catch (error) {
      console.error("removeOneFromBasket error:", error);
      throw error;
    }
  }

  // Полное удаление товаров по массиву ID
  async removeItems(ids, signal) {
    try {
      const newItems = { ...this.items };
      // Асинхронно удаляем элементы из корзины
      await Promise.all(
        ids.map(async (id) => {
          await fetchWithAbort(
            (signal) => basketApi.deleteEvrimaBasketId(id, 999, signal),
            signal
          );
          delete newItems[id];
        })
      );

      // Обновляем состояние корзины после удаления
      this.setItems(newItems);

      // Удаляем идентификаторы из выбранных
      this.removeCheckedItemsIds(ids);
      this.updateTotalBasketCount();
    } catch (err) {
      console.error("removeItems error:", err);
      throw err;
    }
  }

  // Установка количества товара вручную
  async setToBasket(shopItem, amount, signal) {
    try {
      const item = this.items[shopItem.itemId];
      const adjustedAmount = Math.max(
        0,
        Math.min(
          amount,
          basketMaxItemsCount - this.totalBasketItemsCount + (item?.amount || 0)
        )
      );

      console.log("adjustedAmount: ", adjustedAmount, item?.amount);

      if (adjustedAmount !== (item?.amount || 0)) {
        const diff = adjustedAmount - (item?.amount || 0);

        if (diff > 0) {
          await fetchWithAbort(
            (signal) =>
              basketApi.addEvrimaBasketId(shopItem.itemId, diff, signal),
            signal
          );
        } else {
          await fetchWithAbort(
            (signal) =>
              basketApi.deleteEvrimaBasketId(shopItem.itemId, -diff, signal),
            signal
          );
        }

        const updatedItems = {
          ...this.items,
          [shopItem.itemId]: { ...item, shopItem, amount: adjustedAmount },
        };
        this.setItems(updatedItems);
      }
    } catch (error) {
      console.error("setToBasket error:", error);
      throw error;
    }
  }

  // Выбор всех товаров
  selectAllItems(ids) {
    this.checkedItemsIds = new Set(ids);
    this.updatePrices();
  }

  // Снятие выбора со всех товаров
  clearCheckedItems() {
    this.checkedItemsIds = new Set();
    this.updatePrices();
  }

  // Тоггл выбора товара
  toggleCheckedItem(id) {
    const newCheckedItemsIds = new Set(this.checkedItemsIds);
    if (newCheckedItemsIds.has(id)) {
      newCheckedItemsIds.delete(id);
    } else {
      newCheckedItemsIds.add(id);
    }
    this.checkedItemsIds = newCheckedItemsIds;
    this.updatePrices();
  }

  removeCheckedItemsIds(ids) {
    const newCheckedItemsIds = new Set(this.checkedItemsIds);
    ids.forEach((id) => newCheckedItemsIds.delete(id));
    this.checkedItemsIds = newCheckedItemsIds;
    this.updatePrices();
  }

  // Обновление цен на основе выбранных товаров
  updatePrices() {
    const validCheckedItems = [...this.checkedItemsIds].filter(
      (id) => this.items[id]
    );

    this.totalPriceTeeth = validCheckedItems.reduce(
      (sum, id) =>
        sum +
        (this.items[id]?.shopItem.teeth || 0) * (this.items[id]?.amount || 1),
      0
    );

    this.totalPriceOa = validCheckedItems.reduce(
      (sum, id) =>
        sum +
        (this.items[id]?.shopItem.oa || 0) * (this.items[id]?.amount || 1),
      0
    );

    this.checkedItemsIds = new Set(validCheckedItems);
  }

  // Подготовка данных для подтверждения заказа
  prepareForCheckout() {
    return {
      checkedItems: [...this.checkedItemsIds].map((id) => this.items[id]),
      totalPriceTeeth: this.totalPriceTeeth,
      totalPriceOa: this.totalPriceOa,
    };
  }

  // Обновление общего количества товаров в корзине
  updateTotalBasketCount() {
    this.totalBasketItemsCount = Object.values(this.items).reduce(
      (sum, item) => sum + item.amount,
      0
    );
  }

  // Общее количество товаров в корзине
  get itemCount() {
    return Object.keys(this.items).length;
  }

  // просто чтобы нельзя было изменить через консоль
  setIsVisible(isVisible) {
    this.isVisible = isVisible;
  }
}

const basketStore = new BasketStore();
export default basketStore;
